import { useState, useCallback, useRef } from 'react';
import {
  Globe,
  Loader2,
  CheckCircle2,
  Plus,
  Trash2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '../api/coworkApi';
import type { Agent, BrandIdentity } from '../types/cowork';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface WizardCompetitor {
  id: string;
  name: string;
  website: string;
}

export interface CoworkOnboardingProps {
  agents: Agent[];
  onComplete: () => void;
  onSkip: () => void;
}

// ─────────────────────────────────────────────
//  Shared sub-components
// ─────────────────────────────────────────────

function PrimaryBtn({
  children,
  onClick,
  disabled,
  loading,
  dark = false,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  dark?: boolean;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'flex items-center gap-2 px-6 py-3 rounded-xl text-[14px] font-semibold transition-all',
        dark
          ? 'bg-gradient-to-r from-[#be43ff] to-[#7c3aed] text-white hover:opacity-90 disabled:opacity-40'
          : 'bg-[#be43ff] text-white hover:opacity-90 disabled:opacity-40',
        className,
      )}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

function GhostBtn({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#667085] hover:text-[#344054] hover:bg-gray-100 transition-all',
        className,
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-semibold text-[#344054]">{label}</label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Step 1 — Welcome
// ─────────────────────────────────────────────

function StepWelcome({
  agents,
  onStart,
  onSkip,
}: {
  agents: Agent[];
  onStart: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 relative overflow-hidden" style={{ backgroundColor: '#0f0a1e' }}>
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full" style={{ backgroundColor: '#be43ff', opacity: 0.07, filter: 'blur(80px)' }} />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center max-w-[560px] w-full">
        {/* Agent avatar row */}
        <div className="flex items-center gap-2 mb-10 flex-wrap justify-center">
          {agents.map((agent, i) => (
            <div
              key={agent.id}
              className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-lg border border-white/10"
              style={{
                background: `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})`,
                animationDelay: `${i * 60}ms`,
              }}
              title={agent.name}
            >
              {agent.avatarEmoji}
            </div>
          ))}
        </div>

        {/* Headline */}
        <h1
          className="text-white mb-3 leading-tight"
          style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 600 }}
        >
          Meet your AI marketing team
        </h1>
        <p className="text-[15px] leading-relaxed mb-10 max-w-[400px]" style={{ color: '#9b8db8' }}>
          Omni Ad Cowork puts 8 specialist agents to work on your brand — from content and SEO to
          paid media and strategy. Let's get you set up.
        </p>

        {/* CTAs */}
        <div className="flex flex-col items-center gap-3 w-[260px]">
          <PrimaryBtn onClick={onStart} dark className="w-[260px] justify-center text-[15px] py-3.5">
            Set up my brand
            <ArrowRight className="w-4 h-4" />
          </PrimaryBtn>
          <button
            onClick={onSkip}
            className="text-[13px] transition-colors" style={{ color: '#6b5f82' }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Step 2 — Brand Setup
// ─────────────────────────────────────────────

const INDUSTRY_OPTIONS = [
  '', 'Technology', 'E-Commerce', 'Fashion & Apparel', 'Beauty & Wellness',
  'Food & Beverage', 'Health & Fitness', 'Finance & Fintech', 'Entertainment & Media',
  'Music', 'Gaming', 'Travel & Hospitality', 'Education', 'B2B / SaaS', 'Retail',
  'Sports', 'Non-profit', 'Other',
];

// The crawler returns free-text industries ("influencer marketing platform");
// the form uses a fixed list. Map to the closest option so the import
// doesn't silently leave the dropdown empty.
function mapIndustryToOption(raw: string): string {
  const exact = INDUSTRY_OPTIONS.find(o => o.toLowerCase() === raw.toLowerCase());
  if (exact) return exact;
  const r = raw.toLowerCase();
  const KEYWORDS: Array<[RegExp, string]> = [
    [/e-?commerce|online (store|shop)|marketplace/, 'E-Commerce'],
    [/fashion|apparel|clothing/, 'Fashion & Apparel'],
    [/beauty|cosmetic|skincare|wellness/, 'Beauty & Wellness'],
    [/food|beverage|restaurant|drink/, 'Food & Beverage'],
    [/health|fitness|gym|medical/, 'Health & Fitness'],
    [/financ|fintech|bank|insur|payment/, 'Finance & Fintech'],
    [/music/, 'Music'],
    [/gam(e|ing)/, 'Gaming'],
    [/entertain|media|stream/, 'Entertainment & Media'],
    [/travel|hotel|hospitality|tourism/, 'Travel & Hospitality'],
    [/educat|learning|school|course/, 'Education'],
    [/b2b|saas|marketing|advertis|influencer|agency|crm|analytics/, 'B2B / SaaS'],
    [/retail/, 'Retail'],
    [/sport/, 'Sports'],
    [/non-?profit|charity|ngo/, 'Non-profit'],
    [/tech|software|platform|\bai\b|app/, 'Technology'],
  ];
  for (const [re, option] of KEYWORDS) {
    if (re.test(r)) return option;
  }
  return 'Other';
}

function StepBrandSetup({
  brandName, setBrandName,
  website, setWebsite,
  industry, setIndustry,
  description, setDescription,
  crawlStatus, crawlError,
  onCrawl,
  saving,
  onBack,
  onContinue,
}: {
  brandName: string; setBrandName: (v: string) => void;
  website: string; setWebsite: (v: string) => void;
  industry: string; setIndustry: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  crawlStatus: 'idle' | 'crawling' | 'done' | 'error';
  crawlError: string | null;
  onCrawl: () => void;
  saving: boolean;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center min-h-full bg-[#fafafa] px-8 py-12">
      <div className="w-full max-w-[520px]">
        {/* Step header */}
        <div className="mb-7">
          <p className="text-[11px] font-semibold text-[#be43ff] uppercase tracking-widest mb-2">
            Step 2 of 5
          </p>
          <h2 className="text-[#101828] text-[22px] font-semibold leading-tight">
            Tell us about your brand
          </h2>
          <p className="text-[#667085] text-[13px] mt-1.5">
            We'll use this to give all your agents the right context.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-5">
          {/* URL + Crawl */}
          <Field label="Website URL">
            <div className="flex gap-2">
              <input
                type="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onCrawl()}
                placeholder="https://yourbrand.com"
                className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-[#f9fafb] text-[14px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all"
              />
              <button
                onClick={onCrawl}
                disabled={!website.trim() || crawlStatus === 'crawling'}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold transition-all whitespace-nowrap',
                  crawlStatus === 'done'
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    : 'bg-[#be43ff]/10 text-[#be43ff] hover:bg-[#be43ff]/20 disabled:opacity-40',
                )}
              >
                {crawlStatus === 'crawling' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {crawlStatus === 'done' && <CheckCircle2 className="w-3.5 h-3.5" />}
                {crawlStatus !== 'crawling' && crawlStatus !== 'done' && <Globe className="w-3.5 h-3.5" />}
                {crawlStatus === 'crawling' ? 'Importing…' : crawlStatus === 'done' ? 'Imported!' : 'Import'}
              </button>
            </div>
            {crawlError && (
              <p className="flex items-center gap-1.5 text-[12px] text-red-500 mt-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {crawlError} — you can fill in the fields manually.
              </p>
            )}
            {crawlStatus === 'done' && (
              <p className="text-[12px] text-emerald-600 mt-1">
                Brand info imported — review and adjust below if needed.
              </p>
            )}
          </Field>

          {/* Brand name */}
          <Field label="Brand name *">
            <input
              type="text"
              value={brandName}
              onChange={e => setBrandName(e.target.value)}
              placeholder="e.g. Omni Ad"
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 bg-[#f9fafb] text-[14px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all"
            />
          </Field>

          {/* Industry */}
          <Field label="Industry">
            <select
              value={industry}
              onChange={e => setIndustry(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 bg-[#f9fafb] text-[14px] text-[#101828] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all appearance-none"
            >
              {INDUSTRY_OPTIONS.map(o => (
                <option key={o} value={o}>{o || 'Select industry…'}</option>
              ))}
            </select>
          </Field>

          {/* Description */}
          <Field label="What does your brand do?">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="A short description — the agents will reference this constantly."
              rows={3}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 bg-[#f9fafb] text-[14px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all resize-none"
            />
          </Field>
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between mt-5">
          <GhostBtn onClick={onBack}>
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </GhostBtn>
          <PrimaryBtn
            onClick={onContinue}
            disabled={!brandName.trim()}
            loading={saving}
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Step 3 — Meet Your Team
// ─────────────────────────────────────────────

function StepMeetYourTeam({
  agents,
  activeAgentIds,
  togglingAgents,
  onToggle,
  onBack,
  onContinue,
}: {
  agents: Agent[];
  activeAgentIds: Set<string>;
  togglingAgents: Set<string>;
  onToggle: (id: string, current: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col min-h-full bg-[#fafafa]">
      <div className="flex-1 overflow-y-auto px-8 py-12">
        <div className="max-w-[760px] mx-auto">
          {/* Step header */}
          <div className="mb-7">
            <p className="text-[11px] font-semibold text-[#be43ff] uppercase tracking-widest mb-2">
              Step 3 of 5
            </p>
            <h2 className="text-[#101828] text-[22px] font-semibold leading-tight">
              Your team, your way
            </h2>
            <p className="text-[#667085] text-[13px] mt-1.5">
              All 8 agents are active by default. Toggle any off if you don't need them right now — you can always change this later.
            </p>
          </div>

          {/* Agent grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {agents.map(agent => {
              const isActive = activeAgentIds.has(agent.id);
              const isToggling = togglingAgents.has(agent.id);
              return (
                <div
                  key={agent.id}
                  className={cn(
                    'bg-white rounded-2xl border p-4 flex items-start gap-4 transition-all duration-200',
                    isActive
                      ? 'border-gray-100 shadow-sm'
                      : 'border-gray-100 opacity-50',
                  )}
                >
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${agent.gradientFrom}33, ${agent.gradientTo}33)`,
                      border: `1.5px solid ${agent.accentColor}30`,
                    }}
                  >
                    {agent.avatarEmoji}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[14px] text-[#101828] font-semibold">{agent.name}</span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: `${agent.accentColor}15`, color: agent.accentColor, fontWeight: 600 }}
                      >
                        {agent.personalityTraits[0]}
                      </span>
                    </div>
                    <p className="text-[12px] text-[#667085]">{agent.title}</p>
                    <p className="text-[11px] text-[#98a2b3] mt-1 leading-relaxed line-clamp-2">{agent.tagline}</p>
                  </div>

                  {/* Toggle */}
                  <div className="flex items-center shrink-0 mt-0.5">
                    {isToggling ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#c4c9d4]" />
                    ) : (
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => onToggle(agent.id, isActive)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer nav */}
          <div className="flex items-center justify-between">
            <GhostBtn onClick={onBack}>
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </GhostBtn>
            <PrimaryBtn onClick={onContinue}>
              Continue
              <ArrowRight className="w-4 h-4" />
            </PrimaryBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Step 4 — Competitors
// ─────────────────────────────────────────────

function StepCompetitors({
  competitors,
  setCompetitors,
  discoveryStatus,
  discoveryLog,
  onDiscover,
  onBack,
  onContinue,
  onSkip,
}: {
  competitors: WizardCompetitor[];
  setCompetitors: (fn: (prev: WizardCompetitor[]) => WizardCompetitor[]) => void;
  discoveryStatus: 'idle' | 'running' | 'done' | 'error';
  discoveryLog: string[];
  onDiscover: () => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const updateCompetitor = (id: string, field: 'name' | 'website', value: string) => {
    setCompetitors(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const addRow = () => {
    if (competitors.length >= 5) return;
    setCompetitors(prev => [...prev, { id: crypto.randomUUID(), name: '', website: '' }]);
  };

  const removeRow = (id: string) => {
    setCompetitors(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="flex-1 flex flex-col min-h-full bg-[#fafafa]">
      <div className="flex-1 overflow-y-auto px-8 py-12">
        <div className="max-w-[560px] mx-auto">
          {/* Step header */}
          <div className="mb-7">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[11px] font-semibold text-[#be43ff] uppercase tracking-widest">
                Step 4 of 5
              </p>
              <span className="text-[10px] text-[#98a2b3] border border-gray-200 rounded-full px-2 py-0.5 font-medium">
                optional
              </span>
            </div>
            <h2 className="text-[#101828] text-[22px] font-semibold leading-tight">
              Who are your main competitors?
            </h2>
            <p className="text-[#667085] text-[13px] mt-1.5">
              Your agents use competitive context to produce sharper insights and positioning.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4">
            {/* Manual rows */}
            <div className="flex flex-col gap-3">
              {competitors.map((c, idx) => (
                <div key={c.id} className="flex items-center gap-2">
                  <span className="text-[11px] text-[#c4c9d4] w-5 text-center shrink-0 font-medium">
                    {idx + 1}
                  </span>
                  <input
                    type="text"
                    value={c.name}
                    onChange={e => updateCompetitor(c.id, 'name', e.target.value)}
                    placeholder="Competitor name"
                    className="flex-1 px-3 py-2 rounded-xl border border-gray-200 bg-[#f9fafb] text-[13px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all"
                  />
                  <input
                    type="url"
                    value={c.website}
                    onChange={e => updateCompetitor(c.id, 'website', e.target.value)}
                    placeholder="Website (optional)"
                    className="flex-1 px-3 py-2 rounded-xl border border-gray-200 bg-[#f9fafb] text-[13px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]/60 transition-all"
                  />
                  <button
                    onClick={() => removeRow(c.id)}
                    className="text-[#c4c9d4] hover:text-red-400 transition-colors p-1 shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {competitors.length < 5 && (
              <button
                onClick={addRow}
                className="flex items-center gap-1.5 text-[12px] text-[#be43ff] hover:text-[#a020e0] font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add competitor
              </button>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-[11px] text-[#c4c9d4] font-medium">or</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* AI discovery */}
            <div>
              {discoveryStatus === 'idle' && (
                <button
                  onClick={onDiscover}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-[#be43ff]/30 text-[#be43ff] text-[13px] font-semibold hover:bg-[#be43ff]/5 hover:border-[#be43ff]/50 transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  Discover competitors with AI
                </button>
              )}
              {discoveryStatus === 'running' && (
                <div className="rounded-xl border border-[#be43ff]/20 bg-[#faf5ff] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#be43ff]" />
                    <span className="text-[12px] font-semibold text-[#be43ff]">Discovering competitors…</span>
                  </div>
                  <div className="flex flex-col gap-1 max-h-[120px] overflow-y-auto">
                    {discoveryLog.map((msg, i) => (
                      <p key={i} className="text-[11px] text-[#667085] leading-relaxed">{msg}</p>
                    ))}
                  </div>
                </div>
              )}
              {discoveryStatus === 'done' && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span className="text-[13px] text-emerald-700 font-medium">
                    Competitors discovered — review the list above, then continue.
                  </span>
                </div>
              )}
              {discoveryStatus === 'error' && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <span className="text-[12px] text-red-600">
                    Discovery failed. You can add competitors manually above or skip.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-5">
            <GhostBtn onClick={onBack}>
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </GhostBtn>
            <div className="flex items-center gap-3">
              <button
                onClick={onSkip}
                className="text-[13px] text-[#98a2b3] hover:text-[#667085] font-medium transition-colors"
              >
                Skip
              </button>
              <PrimaryBtn onClick={onContinue}>
                Continue
                <ArrowRight className="w-4 h-4" />
              </PrimaryBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Step 5 — Complete
// ─────────────────────────────────────────────

function StepComplete({
  brandName,
  industry,
  activeCount,
  competitorCount,
  onComplete,
}: {
  brandName: string;
  industry: string;
  activeCount: number;
  competitorCount: number;
  onComplete: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 relative overflow-hidden" style={{ backgroundColor: '#0f0a1e' }}>
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] rounded-full" style={{ backgroundColor: '#be43ff', opacity: 0.08, filter: 'blur(80px)' }} />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center max-w-[480px] w-full">
        {/* Check icon */}
        <div className="w-16 h-16 rounded-2xl bg-[#be43ff]/20 border border-[#be43ff]/30 flex items-center justify-center mb-7">
          <Check className="w-8 h-8 text-[#be43ff]" />
        </div>

        <h1
          className="text-white mb-3 leading-tight"
          style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600 }}
        >
          You're all set{brandName ? `, ${brandName}` : ''}!
        </h1>
        <p className="text-[14px] leading-relaxed mb-8" style={{ color: '#9b8db8' }}>
          Your workspace is ready. Your agents have the context they need to start producing results.
        </p>

        {/* Summary */}
        <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 mb-8 flex flex-col gap-3 text-left">
          {brandName && (
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: '#6b5f82' }}>Brand</span>
              <span className="text-[13px] text-white font-semibold">
                {brandName}{industry ? ` · ${industry}` : ''}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[12px]" style={{ color: '#6b5f82' }}>Agents active</span>
            <span className="text-[13px] text-white font-semibold">{activeCount} of 8</span>
          </div>
          {competitorCount > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: '#6b5f82' }}>Competitors tracked</span>
              <span className="text-[13px] text-white font-semibold">{competitorCount}</span>
            </div>
          )}
        </div>

        <PrimaryBtn onClick={onComplete} dark className="px-8 py-3.5 text-[15px]">
          Enter Cowork
          <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Progress indicator
// ─────────────────────────────────────────────

function StepDots({ step }: { step: WizardStep }) {
  return (
    <div className="flex items-center gap-1.5">
      {([1, 2, 3, 4, 5] as WizardStep[]).map(s => (
        <div
          key={s}
          className={cn(
            'rounded-full transition-all duration-300',
            s < step
              ? 'w-2 h-2 bg-[#be43ff]'
              : s === step
              ? 'w-5 h-2 bg-[#be43ff]'
              : 'w-2 h-2 bg-gray-200',
          )}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────

export function CoworkOnboarding({ agents, onComplete, onSkip }: CoworkOnboardingProps) {
  const [step, setStep] = useState<WizardStep>(1);

  // Step 2 state
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [description, setDescription] = useState('');
  const [crawlStatus, setCrawlStatus] = useState<'idle' | 'crawling' | 'done' | 'error'>('idle');
  const [crawlError, setCrawlError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Step 3 state
  const [activeAgentIds, setActiveAgentIds] = useState<Set<string>>(
    () => new Set(agents.map(a => a.id)),
  );
  const [togglingAgents, setTogglingAgents] = useState<Set<string>>(new Set());

  // Step 4 state
  const [competitors, setCompetitors] = useState<WizardCompetitor[]>([
    { id: crypto.randomUUID(), name: '', website: '' },
  ]);
  const [discoveryStatus, setDiscoveryStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [discoveryLog, setDiscoveryLog] = useState<string[]>([]);
  const discoveryCancelled = useRef(false);

  const goToStep = (s: WizardStep) => setStep(s);
  const activeCount = activeAgentIds.size;
  const filledCompetitors = competitors.filter(c => c.name.trim());

  // ── Handlers ──

  const handleCrawl = useCallback(async () => {
    if (!website.trim()) return;
    setCrawlStatus('crawling');
    setCrawlError(null);
    try {
      const info = await coworkApi.brands.crawl(website.trim());
      if (info.name) setBrandName(info.name);
      if (info.industry) setIndustry(mapIndustryToOption(info.industry));
      if (info.description) setDescription(info.description);
      setCrawlStatus('done');
      setTimeout(() => setCrawlStatus(s => s === 'done' ? 'idle' : s), 4000);
    } catch (err) {
      // Raw API errors (JSON parse failures, 500s) mean nothing to the user —
      // log them, show a friendly message.
      console.error('Brand import failed:', err);
      setCrawlError("We couldn't read your website automatically");
      setCrawlStatus('error');
    }
  }, [website]);

  const handleStep2Continue = useCallback(async () => {
    if (!brandName.trim()) return;
    setSaving(true);
    try {
      await coworkApi.brands.update({
        brand: { name: brandName.trim(), industry, website, description } as BrandIdentity,
      });
    } catch { /* silent — don't block the user */ } finally {
      setSaving(false);
    }
    goToStep(3);
  }, [brandName, industry, website, description]);

  const handleToggleAgent = useCallback(async (agentId: string, current: boolean) => {
    const next = !current;
    setActiveAgentIds(prev => {
      const n = new Set(prev);
      next ? n.add(agentId) : n.delete(agentId);
      return n;
    });
    setTogglingAgents(prev => new Set(prev).add(agentId));
    try {
      await coworkApi.agents.update(agentId, { isActive: next });
    } catch {
      // Rollback on failure
      setActiveAgentIds(prev => {
        const n = new Set(prev);
        current ? n.add(agentId) : n.delete(agentId);
        return n;
      });
    } finally {
      setTogglingAgents(prev => { const n = new Set(prev); n.delete(agentId); return n; });
    }
  }, []);

  const handleDiscover = useCallback(async () => {
    discoveryCancelled.current = false;
    setDiscoveryStatus('running');
    setDiscoveryLog([]);
    try {
      await coworkApi.brands.discoverCompetitors(event => {
        if (discoveryCancelled.current) return;
        if (event.type === 'error') {
          setDiscoveryStatus('error');
        } else if (event.type === 'result') {
          setDiscoveryStatus('done');
          // Show what was found — the user should be able to review and
          // prune the list, not trust a black box.
          const graph = event.data as { nodes?: Array<{ id: string; name: string; website?: string }> } | undefined;
          if (graph?.nodes?.length) {
            setCompetitors(prev => {
              const manual = prev.filter(c => c.name.trim());
              const manualNames = new Set(manual.map(c => c.name.trim().toLowerCase()));
              const discovered = graph.nodes!
                .filter(n => n.name && !manualNames.has(n.name.toLowerCase()))
                .map(n => ({ id: n.id || crypto.randomUUID(), name: n.name, website: n.website || '' }));
              return [...manual, ...discovered];
            });
          }
        } else if (event.message) {
          setDiscoveryLog(prev => [...prev, event.message!]);
        }
      });
    } catch {
      if (!discoveryCancelled.current) setDiscoveryStatus('error');
    }
  }, []);

  const handleStep4Continue = useCallback(async () => {
    // Always persist manually-added competitors (backend deduplicates by name)
    if (filledCompetitors.length > 0) {
      try {
        await coworkApi.brands.update({
          competitor: {
            competitors: filledCompetitors.map(c => ({
              id: c.id,
              name: c.name.trim(),
              website: c.website.trim(),
              positioning: '',
              strengths: [],
              weaknesses: [],
            })),
          },
        });
      } catch { /* silent */ }
    }
    goToStep(5);
  }, [filledCompetitors]);

  const handleComplete = useCallback(async () => {
    // Safety merge — ensures brand name is persisted even if step 2 save failed
    try {
      await coworkApi.brands.update({
        brand: { name: brandName, industry, website, description } as BrandIdentity,
      });
    } catch { /* silent */ }
    onComplete();
  }, [brandName, industry, website, description, onComplete]);

  // ── Render ──

  const isDark = step === 1 || step === 5;

  return (
    <div className="flex-1 flex flex-col">
      {/* Progress bar */}
      <div className={cn('shrink-0 h-0.5', isDark ? 'bg-white/10' : 'bg-gray-100')}>
        <div
          className="h-full bg-[#be43ff] transition-all duration-500 ease-out"
          style={{ width: `${(step / 5) * 100}%` }}
        />
      </div>

      {/* Step counter row (hidden on welcome/complete) */}
      {step !== 1 && step !== 5 && (
        <div className="shrink-0 flex items-center justify-center py-4">
          <StepDots step={step} />
        </div>
      )}

      {/* Step content — key forces remount for fade animation */}
      <div key={step} className="onboarding-step flex-1 flex flex-col min-h-0">
        {step === 1 && (
          <StepWelcome agents={agents} onStart={() => goToStep(2)} onSkip={onSkip} />
        )}
        {step === 2 && (
          <StepBrandSetup
            brandName={brandName} setBrandName={setBrandName}
            website={website} setWebsite={setWebsite}
            industry={industry} setIndustry={setIndustry}
            description={description} setDescription={setDescription}
            crawlStatus={crawlStatus} crawlError={crawlError}
            onCrawl={handleCrawl}
            saving={saving}
            onBack={() => goToStep(1)}
            onContinue={handleStep2Continue}
          />
        )}
        {step === 3 && (
          <StepMeetYourTeam
            agents={agents}
            activeAgentIds={activeAgentIds}
            togglingAgents={togglingAgents}
            onToggle={handleToggleAgent}
            onBack={() => goToStep(2)}
            onContinue={() => goToStep(4)}
          />
        )}
        {step === 4 && (
          <StepCompetitors
            competitors={competitors}
            setCompetitors={setCompetitors}
            discoveryStatus={discoveryStatus}
            discoveryLog={discoveryLog}
            onDiscover={handleDiscover}
            onBack={() => goToStep(3)}
            onContinue={handleStep4Continue}
            onSkip={() => goToStep(5)}
          />
        )}
        {step === 5 && (
          <StepComplete
            brandName={brandName}
            industry={industry}
            activeCount={activeCount}
            competitorCount={filledCompetitors.length}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
}
